/**
 * Attack-sequence critical-hit tracking (M7-D1B final).
 *
 * Critical hits belong to ATTACKS (primary + eligible chained attacks), not dice.
 * Consecutive Crit Chance applies across chained attacks within ONE attack sequence
 * (primary resolve through post-attack skills and Chance to Attack Again), then resets.
 *
 * Stacking (locked): accumulate +talentPp for each consecutive crit already landed
 * before the next chained attack rolls (`accumulate_per_consecutive`).
 */

/**
 * How Crit Chance on Consecutive Hits scales.
 * Production is locked to accumulate_per_consecutive.
 * Other values remain available as test overrides only.
 */
export type ConsecutiveCritStackingMode =
  | 'accumulate_per_consecutive'
  /** @deprecated Design rejected; test override only. */
  | 'fixed_on_preceding_crit'
  /** @deprecated Design rejected; test override only. */
  | 'unresolved';

/**
 * Locked M7-D1B final: +talentPp × consecutiveCritsSoFar on the next chained attack.
 * Example (base 20%, talent 3): primary 20% → after crits 23% / 26% / 29%.
 */
export const CONSECUTIVE_CRIT_STACKING_MODE: ConsecutiveCritStackingMode =
  'accumulate_per_consecutive';

export interface AttackSequenceCritState {
  /**
   * Number of consecutive critical hits already landed in this sequence.
   * 0 at sequence start or after any non-crit.
   */
  consecutiveCrits: number;
}

export function createAttackSequenceCritState(): AttackSequenceCritState {
  return { consecutiveCrits: 0 };
}

/**
 * Crit-chance pp bonus for the *next* attack in the sequence.
 * Applied BEFORE that attack rolls.
 * `consecutiveCritsSoFar` is the streak *before* that attack rolls.
 */
export function consecutiveCritBonusPp(args: {
  consecutiveCritsSoFar: number;
  talentPp: number;
  mode?: ConsecutiveCritStackingMode;
}): number {
  const mode = args.mode ?? CONSECUTIVE_CRIT_STACKING_MODE;
  if (args.talentPp <= 0 || args.consecutiveCritsSoFar <= 0) {
    return 0;
  }
  if (mode === 'unresolved') {
    return 0;
  }
  if (mode === 'fixed_on_preceding_crit') {
    return args.talentPp;
  }
  return args.talentPp * args.consecutiveCritsSoFar;
}

export function recordAttackCritOutcome(
  state: AttackSequenceCritState,
  isCrit: boolean,
): AttackSequenceCritState {
  return {
    consecutiveCrits: isCrit ? state.consecutiveCrits + 1 : 0,
  };
}

/** Eligible sources of chained attacks inside a player attack sequence. */
export type ChainedAttackSource =
  | 'flurry'
  | 'twin_strike'
  | 'attack_again';

export interface ChainedAttackHit {
  source: ChainedAttackSource;
  targetId: string;
  damage: number;
  isCrit: boolean;
  targetHpAfter: number;
}
